import FormButton from '@ui/button/FormButton';
import useSiteState from '@app/state/SiteState';
import { DirectSubmitNoticePanel, NOTICE_PANEL_CLASS, useDirectSubmitNoticeChoice } from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/_table/fragment/DirectSubmitNotice';
import { directSubmitNoticeDetail, DirectSubmitNoticeRequest } from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/_table/fragment/DirectSubmitNotice.utilities';
import { formatProviderPriceBlock, isProviderPriceBlock } from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/common/PriceSubmissionBlockedItems.utilities';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { PaperAirplaneIcon, PencilSquareIcon } from '@heroicons/react/16/solid';
import { CheckCircleIcon } from '@heroicons/react/20/solid';
import Utilities from '@util/Utilities';
import CompanyHotel from '@app/model/company_hotel/CompanyHotel';
import PricingAutomationService from '@app/model/pricing-automation/service/PricingAutomationService';
import SubmitMonthlyPricingResultOutputState from '@app/model/pricing-automation/output/SubmitMonthlyPricingResultOutputState';
import SubmitSelectivePricingInputAdjustmentType from '@app/model/pricing-automation/input/SubmitSelectivePricingInputAdjustmentType';
import SubmitPricingResultOutput from '@app/model/pricing-automation/output/SubmitPricingResultOutput';
import { MarketStateGraphQLQueryKeys } from '@app/model/market_state/service/MarketStateGraphQLQueryKeys';
import useToaster from '@app/component/overlays/toaster/ToasterHook';
import { useTranslation } from '@app/component/translation/Translation';
import Spinner from '@app/component/static/Spinner';
import PercentEditor from '@ui/form/input/PercentEditor';
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import Disclosure from '@ui/disclosure/Disclosure';
import { PriceRecommendationState } from '@app/model/market_state/PriceRecommendationState';
import HotelRoomMappingType from '@app/model/market_state/HotelRoomMappingType';
import { countRooms, editorHint, isUntouchedPercent, marketEstimate, pmsRateGroups, PmsRateGroup } from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/_table/fragment/RecommendationPercentPopup.utilities';

// A date row submits every eligible open room type. A room row submits one type.
// The row owns the request state. PercentEditor groups the input and its actions.

// The text of a blocked push, in the style of the bulk failures under the date.
export const blockedText = (result: SubmitPricingResultOutput, currency: CompanyHotel['settings']['main']['currency']) => {
	return result.blockedItems.map(item => {
		if (isProviderPriceBlock(item.errorCode)) {
			return `${item.roomName}: ${formatProviderPriceBlock(item, amount => Utilities.formatAmount(amount, currency))}`;
		}
		const difference = Number(item.differencePercentage);
		return `${item.roomName}: ${Utilities.formatAmount(Number(item.currentPrice), currency, 0)} → ${Utilities.formatAmount(Number(item.submittedPrice), currency, 0)}`
			+ ` (${difference > 0 ? '+' : ''}${Utilities.roundDouble(difference, 0)}%; limit ±${Utilities.roundDouble(Number(item.limitPercentage), 0)}%)`;
	}).join('; ');
};

export type RecommendationSubmitState = {
	enabled: boolean,
	scope: string,
	editorHint: string,
	editing: boolean,
	typed: string,
	typedIsNumber: boolean,
	isPending: boolean,
	previewPrice: number | undefined,
	previewPercent: number,
	// The Hotel Market Price of the row: the base of the Market estimate (R1, R2).
	hotelMarketPrice: number | undefined,
	// The PMS rates of the open states, one group per room type (R5).
	pmsGroups: PmsRateGroup[],
	formatAmount: (amount: number) => string,
	roomTypeName: (type: HotelRoomMappingType) => string,
	setTyped: (value: string) => void,
	openEditor: () => void,
	cancel: () => void,
	noticeNeeded: boolean,
	notice: DirectSubmitNoticeRequest,
	contextKey: string,
	pushAsIs: (expectedContext?: string) => boolean,
	pushEdited: (expectedContext?: string) => boolean,
};

export const useRecommendationSubmit = ({ companyHotel, statDate, marketUpdatedAt, recommendationIds, recommendationStates, recommendedPrice, effectiveChangeRate, hotelMarketPrice, enabled, onBlocked }: {
	companyHotel: CompanyHotel,
	statDate: string,
	marketUpdatedAt: string | undefined,
	recommendationIds: number[],
	// The open states of the row, in the order of the row: the main type first.
	recommendationStates: PriceRecommendationState[],
	recommendedPrice: number | undefined,
	effectiveChangeRate: number | undefined,
	hotelMarketPrice: number | undefined,
	enabled: boolean,
	onBlocked: (text: string | undefined) => void,
}): RecommendationSubmitState => {
	const queryClient = useQueryClient();
	const toaster = useToaster();
	const { t } = useTranslation();
	const { needed: noticeNeeded } = useDirectSubmitNoticeChoice();
	const userId = useSiteState(state => state.principal?.id);
	const [editing, setEditing] = useState(false);
	const cancel = useCallback(() => setEditing(false), []);
	const pending = useRef(false);
	const [typed, setTyped] = useState('');
	const currency = companyHotel.settings.main.currency;
	const rate = effectiveChangeRate ?? 0;
	const shownPercent = Utilities.roundDouble(rate * 100, 0);
	const typedPercent = Number(typed);
	const typedIsNumber = typed.trim() !== '' && Number.isFinite(typedPercent);
	// R6: the pre-fill counts as untouched; the check then does what the plane does.
	const untouched = !editing || !typedIsNumber || isUntouchedPercent(typed, shownPercent);
	const editedPercent = untouched ? undefined : typedPercent;
	const previewPrice = marketEstimate(hotelMarketPrice, recommendedPrice, editedPercent);
	const previewPercent = editing && typedIsNumber ? typedPercent : shownPercent;
	const pmsGroups = pmsRateGroups(recommendationStates, editedPercent);

	// The confirmation acknowledges a specific set of rates and eligibility. Any change
	// closes it, and the dispatch guard also rejects a stale confirmation.
	const contextKey = JSON.stringify({ companyHotelId: companyHotel.id, userId, statDate, marketUpdatedAt,
		enabled, recommendationIds, hotelMarketPrice, recommendedPrice, effectiveChangeRate,
		recommendations: recommendationStates.map(state => ({ id: state.id, creationDate: state.creationDate,
			roomMappingType: state.roomMappingType, effectiveChangeRate: state.effectiveChangeRate,
			rooms: state.minimumBasePriceRoomStates })) });
	const notice: DirectSubmitNoticeRequest = {
		dateLabel: Utilities.formatDate(new Date(`${statDate}T00:00:00`), 'ddd DD/MM'),
		rateCount: countRooms(pmsGroups),
		detail: directSubmitNoticeDetail(countRooms(pmsGroups), recommendationStates.map(state => companyHotel.findRoomMappingTypeName(state.roomMappingType))),
	};

	const submitMutation = useMutation({
		mutationFn: PricingAutomationService.submitSelectivePricing,
		onMutate: () => onBlocked(undefined),
		onSuccess: async result => {
			setEditing(false);
			if (result.state === SubmitMonthlyPricingResultOutputState.SUCCESS) {
				await queryClient.invalidateQueries({ queryKey: MarketStateGraphQLQueryKeys.all });
				toaster.success('Pricing', 'Pricing submitted successfully!');
				return;
			}
			if (result.blockedItems.length > 0) {
				onBlocked(blockedText(result, currency));
				return;
			}
			const error = result.errorCode ?? result.errorMessage;
			onBlocked(error !== undefined && error !== '' ? t(error, ...(result.errorParams ?? [])) : 'The push failed.');
		},
		onError: (error: string) => onBlocked(t(error)),
		onSettled: () => { pending.current = false; },
	});

	const pushAsIs = (expectedContext?: string) => {
		if (!enabled || pending.current || recommendationIds.length === 0 || (expectedContext !== undefined && expectedContext !== contextKey)) return false;
		pending.current = true;
		submitMutation.mutate({ companyHotelId: companyHotel.id, input: { recommendationIds } });
		return true;
	};
	const pushEdited = (expectedContext?: string) => {
		if (!enabled || pending.current || !typedIsNumber || recommendationIds.length === 0 || (expectedContext !== undefined && expectedContext !== contextKey)) return false;
		pending.current = true;
		submitMutation.mutate({
			companyHotelId: companyHotel.id,
			input: untouched
				? { recommendationIds }
				: { recommendationIds, adjustment: typedPercent, adjustmentType: SubmitSelectivePricingInputAdjustmentType.PERCENTAGE },
		});
		return true;
	};
	const openEditor = () => {
		if (!enabled || pending.current) return;
		setTyped(String(shownPercent));
		setEditing(true);
	};

	return {
		scope: `all ${recommendationIds.length} open room ${recommendationIds.length === 1 ? 'type' : 'types'}`,
		editorHint: editorHint(recommendationStates.length, countRooms(pmsGroups)),
		enabled, editing, typed, typedIsNumber, isPending: submitMutation.isPending, previewPrice, previewPercent,
		hotelMarketPrice, pmsGroups, noticeNeeded, notice, contextKey,
		formatAmount: (amount: number) => Utilities.formatAmount(amount, currency, 0),
		roomTypeName: (type: HotelRoomMappingType) => companyHotel.findRoomMappingTypeName(type),
		setTyped, openEditor, cancel, pushAsIs, pushEdited,
	};
};

// The PMS rates of the open states (R5): one row per room, the Base Price and the new price. A Screen Part.
const PmsRateList: React.FunctionComponent<{ state: RecommendationSubmitState }> = ({ state }) => {
	const showTypes = state.pmsGroups.length > 1;
	return <div className="flex flex-col gap-y-1.5">
		{state.pmsGroups.map(group => <div key={group.roomMappingType} className="flex flex-col gap-y-1">
			{showTypes && <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">{state.roomTypeName(group.roomMappingType)}</p>}
			{group.rows.map(row => <div key={row.roomId} className="flex items-baseline justify-between gap-x-2 text-xs leading-4">
				<span className="min-w-0 truncate text-gray-500" title={row.roomName}>{row.roomName}</span>
				<span className="shrink-0 whitespace-nowrap tabular-nums text-gray-700">
					<span className="text-gray-400">{state.formatAmount(row.basePrice)}</span> → <span className="font-medium">{state.formatAmount(row.newPrice)}</span>
				</span>
			</div>)}
		</div>)}
	</div>;
};

// The panel keeps the shared editor outside the fixed-height data row.
//   - the top block is the Market estimate (R1) with its base (R2); no block when the row has no estimate (R3);
//   - the percent field with the hint (R4);
//   - the PMS rates line opens the rooms (R5); it opens by itself when the row has no estimate.
const RecommendationPercentEditor: React.FunctionComponent<{
	state: RecommendationSubmitState,
	close: () => void,
	previewPriceText?: string,
	hotelMarketPriceText?: string,
}> = ({ state, close, previewPriceText, hotelMarketPriceText }) => {
	useEffect(() => state.cancel, [state.cancel]);
	const [openedContext] = useState(state.contextKey);
	const [confirming, setConfirming] = useState(false);
	useEffect(() => { if (openedContext !== state.contextKey) close(); }, [openedContext, state.contextKey, close]);
	if (openedContext !== state.contextKey) return null;
	const submit = () => {
		if (!state.typedIsNumber) return;
		if (state.noticeNeeded) { setConfirming(true); return; }
		state.pushEdited(openedContext); close();
	};
	if (confirming) return <DirectSubmitNoticePanel request={state.notice} contextKey={state.contextKey}
		onCancel={close} onSubmit={state.pushEdited} />;
	const hasEstimate = previewPriceText !== undefined;
	return <div className="w-60 p-3" onBlurCapture={event => {
		// Moving focus to the disclosure stays inside this editor.
		if (event.currentTarget.contains(event.relatedTarget)) event.stopPropagation();
	}}>
		{hasEstimate && <div className="mb-2 tabular-nums">
			<p className="text-sm font-medium text-gray-700">{previewPriceText}</p>
			<p className="text-xs font-normal text-gray-400">Market estimate{hotelMarketPriceText !== undefined ? ` · from ${hotelMarketPriceText}` : ''}</p>
		</div>}
		<PercentEditor value={state.typed}
			onChange={state.setTyped}
			onSubmit={submit}
			onCancel={close}
			disabled={state.isPending || !state.enabled}
			showActions
			submitDisabled={!state.typedIsNumber}
			hint={state.editorHint}
			ariaLabel="Percent change of the recommendation" />
		{state.pmsGroups.length > 0 && <div className="mt-2">
			<Disclosure label="PMS rates" defaultOpen={!hasEstimate} size="small">
				<PmsRateList state={state} />
			</Disclosure>
		</div>}
	</div>;
};

const LINK = 'relative inline-flex items-center justify-center text-gray-500 hover:text-blue-500 cursor-pointer focus:outline-none';

export const RecommendationPercentLine: React.FunctionComponent<{
	state: RecommendationSubmitState | undefined,
	percentText: string,
	className: string,
	rowClassName: string,
	previewPriceText?: string,
	hotelMarketPriceText?: string,
}> = ({ state, percentText, className, rowClassName, previewPriceText, hotelMarketPriceText }) => {
	return <Popover className="contents">
		{({ close }) => <>
			<span className={Utilities.classNames('col-start-1 flex h-4 items-center tabular-nums', rowClassName, className)}>{percentText}</span>
			{state !== undefined && state.enabled && !state.isPending && <>
				<PopoverButton title="Change the percent" onClick={state.openEditor}
					className={Utilities.classNames('col-start-2 relative inline-flex h-4 w-3 items-center justify-center rounded-sm text-gray-400 hover:text-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-600', rowClassName)}>
					<span className="sr-only">Change the percent</span>
					<PencilSquareIcon className="w-3 h-3" aria-hidden="true" />
				</PopoverButton>
				<PopoverPanel anchor="bottom start" portal className="rates-recommendation-editor z-50 mt-2 w-max max-w-[calc(100vw-16px)] rounded-md bg-white text-left text-sm shadow-lg ring-1 ring-black ring-opacity-5">
					<RecommendationPercentEditor state={state} close={close} previewPriceText={previewPriceText} hotelMarketPriceText={hotelMarketPriceText} />
				</PopoverPanel>
			</>}
		</>}
	</Popover>;
};

export const RecommendationRightControl: React.FunctionComponent<{
	state: RecommendationSubmitState | undefined,
	isSubmitted: boolean,
}> = ({ state, isSubmitted }) => {
	if (isSubmitted) {
		return <span className="relative inline-flex items-center justify-center text-blue-600" title="Submitted">
			<span className="sr-only">Submitted</span>
			<CheckCircleIcon className="h-4 w-4" aria-hidden="true" />
		</span>;
	}
	if (state === undefined || !state.enabled) return null;
	if (state.isPending) return <span className="inline-flex items-center justify-center"><Spinner width={4} height={4} /></span>;
	if (state.editing) return null;
	if (state.noticeNeeded) return <Popover className="relative inline-flex">
		{({ close }) => <>
			<span title={`Submit ${state.scope}`} className="relative inline-flex">
				<PopoverButton className={LINK}>
					<span className="sr-only">Submit {state.scope}</span>
					<PaperAirplaneIcon className="w-4 h-4" aria-hidden="true" />
				</PopoverButton>
			</span>
			<PopoverPanel anchor="bottom end" portal className={NOTICE_PANEL_CLASS}>
				<DirectSubmitNoticePanel request={state.notice} contextKey={state.contextKey}
					onCancel={close} onSubmit={state.pushAsIs} />
			</PopoverPanel>
		</>}
	</Popover>;
	return <span title={`Submit ${state.scope}`} className="relative inline-flex">
		<FormButton state="text" onClick={() => { state.pushAsIs(); }} className={Utilities.classNames(LINK, 'p-0 shadow-none')}>
			<span className="sr-only">Submit {state.scope}</span>
			<PaperAirplaneIcon className="w-4 h-4" aria-hidden="true" />
		</FormButton>
	</span>;
};
